import {useObservableValueRef} from '@github-ui/react-observable'
import {useLayoutEffect} from '@github-ui/use-layout-effect'
import {createContext, type PropsWithChildren, useContext, useReducer} from 'react'

import type {CopilotChatOrg} from '../../utils/copilot-chat-types'
import {ObservableTraceStateProvider} from './ObservableTraceContext'
import {TraceManagerProvider} from './TraceManagerContext'
import {type TraceAction, traceReducer, type TraceState} from './utilities/trace-reducer'

const TraceStateContext = createContext<TraceState | null>(null)
const TraceDispatchContext = createContext<React.Dispatch<TraceAction> | null>(null)

export function TraceProvider({
  apiURL,
  children,
  ssoOrgs,
  testReducerState,
}: PropsWithChildren<{apiURL: string; ssoOrgs: CopilotChatOrg[]; testReducerState?: TraceState}>) {
  const initialState: TraceState = testReducerState || {}
  const [state, dispatch] = useReducer(traceReducer, initialState)
  const [stateObservable, setStateObservable] = useObservableValueRef(state)

  // When the state updates, update the value of the observable
  useLayoutEffect(() => {
    if (state !== stateObservable.current.value) {
      setStateObservable(state)
    }
  }, [state, stateObservable, setStateObservable])

  return (
    <TraceStateContext value={state}>
      <TraceDispatchContext value={dispatch}>
        <ObservableTraceStateProvider value={stateObservable}>
          <TraceManagerProvider apiURL={apiURL} dispatch={dispatch} ssoOrgs={ssoOrgs}>
            {children}
          </TraceManagerProvider>
        </ObservableTraceStateProvider>
      </TraceDispatchContext>
    </TraceStateContext>
  )
}

export function useTraceState() {
  const traceState = useContext(TraceStateContext)
  if (!traceState) {
    throw new Error('useTraceState must be used within a TraceContext')
  }
  return traceState
}

export function useTraceDispatchMaybe() {
  return useContext(TraceDispatchContext)
}

export function useTraceDispatch() {
  const dispatch = useContext(TraceDispatchContext)
  if (!dispatch) {
    throw new Error('useTraceDispatch must be used within a TraceContext')
  }
  return dispatch
}

try{ TraceStateContext.displayName ||= 'TraceStateContext' } catch {}
try{ TraceDispatchContext.displayName ||= 'TraceDispatchContext' } catch {}
try{ TraceProvider.displayName ||= 'TraceProvider' } catch {}